import EditableList from '@/components/EditableList'
import ProfileRow from '@/components/Profile/ProfileRow'
import { useFetchFollowings } from '@/hooks/useFetchFollowings'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { username } from '@/lib/event-metadata'
import { unwrappedFollowListAtom } from '@/atoms/lists'
import { currentPubkeyAtom } from '@/atoms/session'
import client from '@/services/client.service'
import { invalidateFollowList } from '@/atoms/invalidation'
import { useAtomValue } from 'jotai'
import * as nip19 from '@nostr/tools/nip19'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { loadFollowsList } from '@nostr/gadgets/lists'
import { sampleTopFromFollows } from '@/lib/recommendations'

const FollowingListPage = forwardRef(({ id, index }: { id: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useAtomValue(currentPubkeyAtom)
  const { profile } = useFetchProfile(id)
  const { followings } = useFetchFollowings(profile?.pubkey)
  const followList = useAtomValue(unwrappedFollowListAtom)

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || username(profile) || '...'
    document.title = t("{{name}}'s following on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])

  if (!profile) return null

  const name = username(profile)
  const isSelf = currentPubkey === profile.pubkey

  async function loadRecommendations(): Promise<string[]> {
    if (!currentPubkey) return []
    const exclude = new Set([...(followList ?? []), currentPubkey])
    return sampleTopFromFollows<string>(
      currentPubkey,
      async (pk) => (await loadFollowsList(pk)).items,
      (pk) => pk,
      exclude
    )
  }

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={name ? t("{{username}}'s following", { username: name }) : t('Following')}
      displayScrollToTopButton
    >
      {isSelf ? (
        <div className="px-4 py-3">
          <EditableList
            items={followList ?? []}
            renderItem={(pubkey) => <ProfileRow pubkey={pubkey} />}
            validateItem={(val) => {
              let hex: string
              if (val.startsWith('npub') || val.startsWith('nprofile')) {
                const decoded = nip19.decode(val)
                hex = decoded.data as string
              } else if (/^[0-9a-f]{64}$/i.test(val)) {
                hex = val
              } else {
                return { ok: false as const, error: t('Invalid pubkey') }
              }
              return { ok: true as const, item: hex }
            }}
            onUpdate={async (newList: string[]) => {
              if (!currentPubkey) return
              const tags = newList.map((pk) => ['p', pk])
              const event = await client.publish({
                kind: 3,
                tags,
                content: '',
                created_at: Math.floor(Date.now() / 1000)
              })
              if (!event) {
                console.warn('failed to publish updated following list event')
                toast.error(t('Failed to publish updated following list event.'))
                return
              }
              await loadFollowsList(event.pubkey, undefined, event)
              invalidateFollowList()
            }}
            placeholder={t('Enter npub or hex')}
            emptyMessage={t('Not following anyone')}
            loadRecommendations={loadRecommendations}
          />
        </div>
      ) : (
        <div className="px-4 py-3">
          <EditableList
            items={followings}
            editable={false}
            renderItem={(pubkey) => <ProfileRow pubkey={pubkey} />}
            validateItem={() => ({ ok: false as const, error: '' })}
            onUpdate={async () => {}}
            emptyMessage={t('Not following anyone')}
          />
        </div>
      )}
    </SecondaryPageLayout>
  )
})
FollowingListPage.displayName = 'FollowingListPage'
export default FollowingListPage
